import { useMutation } from "@tanstack/react-query";
import { api, type Api } from "@api/base-api-request";
import { AuthResponse, AuthRequestParams } from "./types";
import { setApiAccessToken } from "@/helpers/localStorage";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { useNavigate } from "react-router-dom";

const MUTATION_KEY_WALLET = "auth-wallet";
const MUTATION_KEY_SOCIAL = "auth-social";

const authenticateWithWallet = async (
  api: Api,
  params: AuthRequestParams,
): Promise<AuthResponse> => {
  return api.post("auth/web3/external-wallet", { json: params }).json();
};

const authenticateWithSocial = async (
  api: Api,
  params: AuthRequestParams,
): Promise<AuthResponse> => {
  return api.post("auth/web3/social", { json: params }).json();
};

const useAuthenticateWithWallet = () =>
  useMutation({
    mutationKey: [MUTATION_KEY_WALLET, "web3"],
    mutationFn: (payload: AuthRequestParams) =>
      authenticateWithWallet(api, payload),
  });

const useAuthenticateWithSocial = () =>
  useMutation({
    mutationKey: [MUTATION_KEY_SOCIAL, "web3"],
    mutationFn: (payload: AuthRequestParams) =>
      authenticateWithSocial(api, payload),
    onSuccess: (data) => {
      setApiAccessToken(data.accessToken);
    },
  });

const authenticateAdminWithSocial = async (
  api: Api,
  params: AuthRequestParams,
): Promise<AuthResponse> => {
  return api.post("auth/web3/admin", { json: params }).json();
};

const useAuthenticateAdminWithSocial = () =>
  useMutation({
    mutationKey: [MUTATION_KEY_SOCIAL, "web3"],
    mutationFn: (payload: AuthRequestParams) =>
      authenticateAdminWithSocial(api, payload),
  });

export const useLogout = () => {
  const { logout } = useWeb3Auth();
  const navigate = useNavigate();

  return async () => {
    try {
      await logout();
    } catch (e) {
      console.error("Web3Auth logout failed:", e);
    }

    setApiAccessToken("");

    localStorage.clear();

    navigate("/admin");
  };
};

export {
  useAuthenticateWithWallet,
  useAuthenticateWithSocial,
  authenticateWithWallet,
  authenticateWithSocial,
  authenticateAdminWithSocial,
  useAuthenticateAdminWithSocial,
  MUTATION_KEY_WALLET as AUTH_WALLET_MUTATION_KEY,
  MUTATION_KEY_SOCIAL as AUTH_SOCIAL_MUTATION_KEY,
  type AuthRequestParams as AuthPayload,
  type AuthResponse as AuthResponse,
};
